import {
  DOM_SETTLE_QUIET_MS,
  DOM_SETTLE_TIMEOUT_MS,
  POLL_INTERVAL_MS,
  POLL_MAX_ATTEMPTS,
  PULSE_HIGHLIGHT_DURATION_MS,
} from './UniversalSearch.constants';

export const getShortcutPrefix = () => {
  const platform = globalThis?.navigator?.platform;
  const isMac = platform?.toUpperCase().includes('MAC');

  return isMac ? '⌘' : 'Ctrl+';
};

const pollForElement = ({ selector, onFound, attempt = 0 }) => {
  const element = document.querySelector(selector);

  if (element) {
    onFound(element);

    return;
  }

  if (attempt >= POLL_MAX_ATTEMPTS) {
    return;
  }

  setTimeout(() => {
    pollForElement({ selector, onFound, attempt: attempt + 1 });
  }, POLL_INTERVAL_MS);
};

const getHighlightTarget = element => {
  const inputBase = element.closest('.MuiInputBase-root');

  if (inputBase) {
    return inputBase;
  }

  const formGroup = element.closest('.MuiFormGroup-root');

  if (formGroup) {
    return formGroup;
  }

  const radioGroup = element.closest('[role="radiogroup"]');

  if (radioGroup) {
    return radioGroup;
  }

  return element;
};

const waitForDomSettle = ({ container = document.body } = {}) =>
  new Promise(resolve => {
    const state = { quietTimer: null, settled: false };

    const settle = observer => {
      if (state.settled) {
        return;
      }

      state.settled = true;
      observer.disconnect();
      resolve();
    };

    const observer = new MutationObserver(() => {
      clearTimeout(state.quietTimer);
      state.quietTimer = setTimeout(() => settle(observer), DOM_SETTLE_QUIET_MS);
    });

    observer.observe(container, { childList: true, subtree: true, attributes: true });

    state.quietTimer = setTimeout(() => settle(observer), DOM_SETTLE_QUIET_MS);

    setTimeout(() => settle(observer), DOM_SETTLE_TIMEOUT_MS);
  });

const applyHighlight = ({ element, highlightTarget }) => {
  highlightTarget.scrollIntoView({ behavior: 'smooth', block: 'center' });

  const focusableElement = element.querySelector('input, select, textarea') || element;
  focusableElement.focus();

  highlightTarget.style.setProperty('transition', 'box-shadow 0.3s ease', 'important');
  highlightTarget.style.setProperty('box-shadow', '0 0 0 3px rgba(59, 140, 255, 0.4)', 'important');
  highlightTarget.style.setProperty('border-radius', '4px', 'important');

  setTimeout(() => {
    highlightTarget.style.removeProperty('box-shadow');
    highlightTarget.style.removeProperty('transition');
    highlightTarget.style.removeProperty('border-radius');
  }, PULSE_HIGHLIGHT_DURATION_MS);
};

export const focusAndHighlightField = ({ fieldId }) => {
  const selector = [
    `[data-field-id="${fieldId}"]`,
    `[id="${fieldId}"]`,
    `[name="${fieldId}"]`,
    `[data-test="${fieldId}"]`,
    `[name*="${fieldId}"]`,
  ].join(', ');

  pollForElement({
    selector,
    onFound: async () => {
      await waitForDomSettle();

      const freshElement = document.querySelector(selector);

      if (!freshElement) {
        return;
      }

      const freshHighlightTarget = getHighlightTarget(freshElement);
      applyHighlight({ element: freshElement, highlightTarget: freshHighlightTarget });
    },
  });
};
